/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  Schedule,
  Schedule$inboundSchema,
  Schedule$Outbound,
  Schedule$outboundSchema,
} from "./schedule.js";
import {
  Sequence,
  Sequence$inboundSchema,
  Sequence$Outbound,
  Sequence$outboundSchema,
} from "./sequence.js";

/**
 * The kind of the reading or setting value. This is included so we can render something specific to the kind before we have any values.
 */
export const SettingActionValueKind = {
  Number: "number",
  Boolean: "boolean",
  Enum: "enum",
  Schedule: "schedule",
  Sequence: "sequence",
  String: "string",
} as const;
/**
 * The kind of the reading or setting value. This is included so we can render something specific to the kind before we have any values.
 */
export type SettingActionValueKind = OpenEnum<typeof SettingActionValueKind>;

/**
 * A data driven action that is driven by a setting. This specifies a value that will be used to drive the action.
 */
export type SettingAction = {
  /**
   * The boolean value of the setting.
   */
  booleanValue?: boolean | undefined;
  /**
   * Whether the action is custom. If true, the value is computed by the client.
   */
  customValue?: boolean | undefined;
  /**
   * The enum value of the setting.
   */
  enumValue?: string | undefined;
  /**
   * The id of the reading or setting
   */
  id: string;
  /**
   * The key of the reading or setting.
   */
  key?: string | undefined;
  /**
   * The name of the action. Helps to debug the action in the UI.
   */
  name?: string | undefined;
  /**
   * The number value of the setting.
   */
  numberValue?: number | undefined;
  /**
   * The schedule value of
   */
  scheduleValue?: Schedule | undefined;
  /**
   * The sequence value
   */
  sequenceValue?: Sequence | undefined;
  /**
   * The units of the reading or setting. This is included so we can render units before we have any values.
   */
  units?: string | undefined;
  /**
   * The kind of the reading or setting value. This is included so we can render something specific to the kind before we have any values.
   */
  valueKind: SettingActionValueKind;
};

/** @internal */
export const SettingActionValueKind$inboundSchema: z.ZodType<
  SettingActionValueKind,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(SettingActionValueKind);
/** @internal */
export const SettingActionValueKind$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  SettingActionValueKind
> = openEnums.outboundSchema(SettingActionValueKind);

/** @internal */
export const SettingAction$inboundSchema: z.ZodType<
  SettingAction,
  z.ZodTypeDef,
  unknown
> = z.object({
  boolean_value: types.optional(types.boolean()),
  custom_value: types.optional(types.boolean()),
  enum_value: types.optional(types.string()),
  id: types.string(),
  key: types.optional(types.string()),
  name: types.optional(types.string()),
  number_value: types.optional(types.number()),
  schedule_value: types.optional(Schedule$inboundSchema),
  sequence_value: types.optional(Sequence$inboundSchema),
  units: types.optional(types.string()),
  value_kind: SettingActionValueKind$inboundSchema,
}).transform((v) => {
  return remap$(v, {
    "boolean_value": "booleanValue",
    "custom_value": "customValue",
    "enum_value": "enumValue",
    "number_value": "numberValue",
    "schedule_value": "scheduleValue",
    "sequence_value": "sequenceValue",
    "value_kind": "valueKind",
  });
});
/** @internal */
export type SettingAction$Outbound = {
  boolean_value?: boolean | undefined;
  custom_value?: boolean | undefined;
  enum_value?: string | undefined;
  id: string;
  key?: string | undefined;
  name?: string | undefined;
  number_value?: number | undefined;
  schedule_value?: Schedule$Outbound | undefined;
  sequence_value?: Sequence$Outbound | undefined;
  units?: string | undefined;
  value_kind: string;
};

/** @internal */
export const SettingAction$outboundSchema: z.ZodType<
  SettingAction$Outbound,
  z.ZodTypeDef,
  SettingAction
> = z.object({
  booleanValue: z.boolean().optional(),
  customValue: z.boolean().optional(),
  enumValue: z.string().optional(),
  id: z.string(),
  key: z.string().optional(),
  name: z.string().optional(),
  numberValue: z.number().optional(),
  scheduleValue: Schedule$outboundSchema.optional(),
  sequenceValue: Sequence$outboundSchema.optional(),
  units: z.string().optional(),
  valueKind: SettingActionValueKind$outboundSchema,
}).transform((v) => {
  return remap$(v, {
    booleanValue: "boolean_value",
    customValue: "custom_value",
    enumValue: "enum_value",
    numberValue: "number_value",
    scheduleValue: "schedule_value",
    sequenceValue: "sequence_value",
    valueKind: "value_kind",
  });
});

export function settingActionToJSON(settingAction: SettingAction): string {
  return JSON.stringify(SettingAction$outboundSchema.parse(settingAction));
}
export function settingActionFromJSON(
  jsonString: string,
): SafeParseResult<SettingAction, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SettingAction$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SettingAction' from JSON`,
  );
}
